import { Building2, Check, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { SidebarContent } from "./Sidebar";
import { useSidebarStore } from "@/store/useSidebarStore";
import { useUserStore } from "@/store/useUserStore";
import { useShowWorkspaceSwitcher } from "@/hooks/useGroupedNav";
import { useBrandingReminderStore } from "@/store/useBrandingReminderStore";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { ReloadButton } from "@/components/portal/common/ReloadButton";
import { GlobalSearch } from "@/components/portal/layout/GlobalSearch";
import { NotificationBell } from "@/components/portal/common/dashboard/NotificationBell";
import { EmailNotificationsModal } from "@/components/portal/partials/notifications/EmailNotificationsModal";
import { useEmailNotifications } from "@/hooks/notifications/useEmailNotifications";
import { BugReportLauncher } from "@/components/portal/common/bug-report/BugReportLauncher";
import { TimeOverrideBadge } from "@/components/portal/common/time-override/TimeOverrideBadge";

export const Topbar = () => {
  const { isOpen, onOpenChange, toggle, isCollapsed, toggleCollapse } = useSidebarStore();
  const workspaceSwitcherOpen = useSidebarStore((state) => state.workspaceSwitcherOpen);
  const setWorkspaceSwitcherOpen = useSidebarStore((state) => state.setWorkspaceSwitcherOpen);
  const user = useUserStore((state) => state.user);
  const isSuperAdmin = useUserStore((state) => state.isSuperAdmin);
  const isPartner = useUserStore((state) => state.isPartner);
  const selectedWorkspaceId = useUserStore((state) => state.selectedWorkspaceId);
  const setSelectedWorkspaceId = useUserStore((state) => state.setSelectedWorkspaceId);
  const workspaces = useUserStore((state) => state.workspaces);
  const fetchWorkspaces = useUserStore((state) => state.fetchWorkspaces);
  const fetchPartnerWorkspaces = useUserStore((state) => state.fetchPartnerWorkspaces);

  // Multi-workspace partners get the same switcher super admins have.
  const showWorkspaceSwitcher = useShowWorkspaceSwitcher();
  const brandingChecking = useBrandingReminderStore((state) => state.checking);
  
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const { data: notificationsData } = useEmailNotifications(
    { page: 1, per_page: 1 },
    { enabled: !isSuperAdmin }
  );
  const unreadCount = notificationsData?.data?.unread_count ?? 0;
  
  // For system admins, use user.name, otherwise use user_profile.full_name
  const displayName = isSuperAdmin
    ? user?.user.name
    : user?.user_profile?.full_name || user?.user?.name;

  const firstChar = displayName?.charAt(0).toUpperCase() || "U";

  // Super admins keep their avatar centrally; everyone else in user_profile.
  const avatarUrl = isSuperAdmin
    ? user?.super_admin_avatar_url
    : user?.user_profile?.avatar_url;

  const selectedWorkspace = workspaces.find((w) => w.id === selectedWorkspaceId);

  // Load the workspace list for the switcher (only if not already loaded).
  // Super admins get the full list; partners get only their owned workspaces
  // (partner-scoped endpoint) so multi-workspace partners can switch context.
  useEffect(() => {
    if (workspaces.length > 0) return;
    if (!isSuperAdmin && !isPartner) return;

    const loadWorkspaces = async () => {
      setLoadingWorkspaces(true);
      if (isSuperAdmin) {
        await fetchWorkspaces();
      } else {
        await fetchPartnerWorkspaces();
      }
      setLoadingWorkspaces(false);
    };

    loadWorkspaces();
  }, [isSuperAdmin, isPartner, workspaces.length, fetchWorkspaces, fetchPartnerWorkspaces]);




  return (
    <header className="h-16 bg-white border-b border-zinc-200 flex items-center justify-between px-3 sm:px-4 lg:px-8 shrink-0 z-30 gap-2">

      {/* Left: navigation toggles only (content-sized so the right group can
          grow into the remaining width). */}
      <div className="flex items-center gap-4 shrink-0">

          <button
            className="cursor-pointer lg:hidden flex h-10 w-10 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 transition-colors"
            aria-label="Open navigation menu"
            onClick={toggle}
          >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M4.20374 18H15.2037C15.7537 18 16.2037 17.55 16.2037 17C16.2037 16.45 15.7537 16 15.2037 16H4.20374C3.65374 16 3.20374 16.45 3.20374 17C3.20374 17.55 3.65374 18 4.20374 18ZM4.20374 13H12.2037C12.7537 13 13.2037 12.55 13.2037 12C13.2037 11.45 12.7537 11 12.2037 11H4.20374C3.65374 11 3.20374 11.45 3.20374 12C3.20374 12.55 3.65374 13 4.20374 13ZM3.20374 7C3.20374 7.55 3.65374 8 4.20374 8H15.2037C15.7537 8 16.2037 7.55 16.2037 7C16.2037 6.45 15.7537 6 15.2037 6H4.20374C3.65374 6 3.20374 6.45 3.20374 7ZM20.5037 14.88L17.6237 12L20.5037 9.12C20.8937 8.73 20.8937 8.1 20.5037 7.71C20.1137 7.32 19.4837 7.32 19.0937 7.71L15.5037 11.3C15.1137 11.69 15.1137 12.32 15.5037 12.71L19.0937 16.3C19.4837 16.69 20.1137 16.69 20.5037 16.3C20.8837 15.91 20.8937 15.27 20.5037 14.88Z" fill="currentColor"/>
              </svg>
          </button>
        
        
        <Sheet open={isOpen} onOpenChange={onOpenChange}>
          <SheetContent side="left" className="p-0 w-72">
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
            <SheetDescription className="sr-only">
              Access navigation links and menu options
            </SheetDescription>
            <SidebarContent forceExpanded />
          </SheetContent>
        </Sheet>

        <div className="flex items-center gap-4">
          {/* Expand Trigger (Visible only when sidebar is collapsed on desktop) */}
         

          <button
            onClick={toggleCollapse}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn("cursor-pointer hidden h-10 w-10 items-center justify-center rounded-full text-zinc-500 hover:text-brand-700 hover:bg-zinc-100 transition-colors", isCollapsed && "lg:flex")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              className={cn("transition-transform duration-200", isCollapsed && "-scale-x-100")}
            >
              <path d="M4.20374 18H15.2037C15.7537 18 16.2037 17.55 16.2037 17C16.2037 16.45 15.7537 16 15.2037 16H4.20374C3.65374 16 3.20374 16.45 3.20374 17C3.20374 17.55 3.65374 18 4.20374 18ZM4.20374 13H12.2037C12.7537 13 13.2037 12.55 13.2037 12C13.2037 11.45 12.7537 11 12.2037 11H4.20374C3.65374 11 3.20374 11.45 3.20374 12C3.20374 12.55 3.65374 13 4.20374 13ZM3.20374 7C3.20374 7.55 3.65374 8 4.20374 8H15.2037C15.7537 8 16.2037 7.55 16.2037 7C16.2037 6.45 15.7537 6 15.2037 6H4.20374C3.65374 6 3.20374 6.45 3.20374 7ZM20.5037 14.88L17.6237 12L20.5037 9.12C20.8937 8.73 20.8937 8.1 20.5037 7.71C20.1137 7.32 19.4837 7.32 19.0937 7.71L15.5037 11.3C15.1137 11.69 15.1137 12.32 15.5037 12.71L19.0937 16.3C19.4837 16.69 20.1137 16.69 20.5037 16.3C20.8837 15.91 20.8937 15.27 20.5037 14.88Z" fill="currentColor"/>
            </svg>
          </button>

        </div>
      </div>

      {/* Right: all top-bar icons in order — search, reload, bug report,
          notifications, workspace, profile. Left cluster's flex-1 keeps this
          group pinned to the right edge. */}
      <div className="flex flex-1 items-center justify-end gap-4 sm:gap-6 relative">
        {/* A. Search — grows to fill the remaining width when activated. */}
        <GlobalSearch />

        {/* B. Reload */}
        <ReloadButton
          variant="page"
          title="Reload current page data"
          ariaLabel="Reload current page data"
          size={22}
          className="text-zinc-500 hover:text-brand-700"
        />
        <TimeOverrideBadge />
        <BugReportLauncher />
        <EmailNotificationsModal
          open={notificationsOpen}
          onOpenChange={setNotificationsOpen}
        >
          <button
            type="button"
            aria-label="Email notifications"
            className="relative inline-flex h-10 w-10 items-center justify-center cursor-pointer rounded-full bg-transparent border-0 p-0 text-zinc-500 hover:bg-zinc-100 hover:text-brand-700 transition-colors"
          >
            <NotificationBell size={24} color="currentColor" />
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 min-w-4 h-4 px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full border-2 border-white leading-none">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
        </EmailNotificationsModal>

        {/* E. Workspace switcher (super admins + multi-workspace partners) —
            avatar-style icon that shows the active workspace's logo, with logos
            in the dropdown too. */}
        {showWorkspaceSwitcher && (
          <DropdownMenu open={workspaceSwitcherOpen} onOpenChange={setWorkspaceSwitcherOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Switch workspace"
                title={selectedWorkspace?.name ?? "Select workspace"}
                className="flex items-center cursor-pointer focus:outline-none"
              >
                {loadingWorkspaces ? (
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-50">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  </span>
                ) : (
                  <Avatar className="h-9 w-9 border-2 border-white shadow-sm transition-transform active:scale-95">
                    <AvatarImage
                      src={selectedWorkspace?.logo_url ?? undefined}
                      alt={selectedWorkspace?.name ?? "workspace"}
                    />
                    <AvatarFallback className="bg-brand-50 text-brand-700">
                      <Building2 className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>
                )}
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-64">
              <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                Workspace
              </div>
              {workspaces.map((workspace) => {
                const isCurrent = workspace.id === selectedWorkspaceId;
                return (
                  <DropdownMenuItem
                    key={workspace.id}
                    className={cn(
                      "flex items-center gap-2 p-2 mb-1 last:mb-0 rounded-md border",
                      isCurrent
                        ? "bg-gray-50 border-gray-200 cursor-default"
                        : "border-transparent hover:bg-gray-50"
                    )}
                    disabled={isCurrent}
                    onClick={() => setSelectedWorkspaceId(workspace.id)}
                  >
                    {workspace.logo_url ? (
                      <img
                        src={workspace.logo_url}
                        alt={`${workspace.name} logo`}
                        className="h-7 w-7 shrink-0 rounded-md object-cover border border-gray-100"
                      />
                    ) : (
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-700">
                        <Building2 size={15} />
                      </span>
                    )}
                    <span className="flex-1 min-w-0">
                      <span className={cn("block text-sm truncate", isCurrent ? "font-semibold text-gray-900" : "font-medium text-gray-700")}>
                        {workspace.name}
                      </span>
                      <span className="block text-xs text-gray-400">ID: {workspace.id}</span>
                    </span>
                    {isCurrent && <Check size={16} className="text-gray-500 shrink-0" />}
                  </DropdownMenuItem>
                );
              })}

              {isSuperAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/portal/workspaces" className="cursor-pointer w-full text-sm font-medium text-gray-700 px-2 py-1.5">
                      Manage workspaces
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* F. Profile */}
        <Link to="/portal/profile" className="flex items-center gap-3">
          <div className="relative">
            <Avatar className="h-9 w-9 border-2 border-white shadow-sm cursor-pointer transition-transform active:scale-95">
              <AvatarImage src={avatarUrl ?? undefined} alt={displayName ?? 'profile'} />
              <AvatarFallback className="bg-linear-to-br from-brand-800 to-brand-500 text-white font-bold">
                {firstChar}
              </AvatarFallback>
            </Avatar>
            {brandingChecking && (
              <span
                aria-label="Checking profile setup"
                title="Checking profile setup…"
                className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-black/40"
              >
                <Loader2 className="h-4 w-4 animate-spin text-white" />
              </span>
            )}
          </div>
        </Link>
      </div>
    </header>
  );
};